import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCheckboxMarkedCircle, mdiClock, mdiDownload } from "@mdi/js";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type {
  DocumentProps,
  ServiceQuotation,
} from "../../../../../@types/services/ServiceQuotation";
import type { ServiceQuotationApproval } from "../../../../../@types/services/ServiceQuotationApproval";
import { listServiceNegotiations } from "../../../../../__generated__/orval/endpoints/service-negotiations/service-negotiations";
import { getServiceQuotationDocuments } from "../../../../../__generated__/orval/endpoints/service-quotations/service-quotations";
import type {
  EntitiesServiceNegotiationCollectionListResponseOutput,
  ListServiceNegotiationsParams,
} from "../../../../../__generated__/orval/models";
import { BasicBar } from "../../../../../components/molecules/BasicBar";
import { LoadingCentral } from "../../../../../components/molecules/LoadingCentral";
import { ServiceNegotiationAudits } from "../../../../../components/molecules/ServiceNegotiationAudits";
import {
  getServiceQuotationApproval,
  updateServiceQuotationApproval,
} from "../../../../../services/serviceQuotationApprovals";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";

type ServiceNegotiation = NonNullable<
  EntitiesServiceNegotiationCollectionListResponseOutput["results"]
>[number];

type FormServiceQuotationApprovalProps = {
  serviceQuotationApproval: ServiceQuotationApproval;
};

type ServiceBidWinnerJustificationProps = {
  serviceQuotation: ServiceQuotation;
};

type ScopeOfWorkDocument = Pick<
  DocumentProps,
  "created_at" | "filename" | "is_main" | "mail_user" | "url"
> & {
  id: number | string;
};

const ServiceQuotationScopeOfWorkDocuments: React.FC<
  ServiceBidWinnerJustificationProps
> = ({ serviceQuotation }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<DocumentProps[]>([]);

  const loadDocuments = useCallback((): void => {
    getServiceQuotationDocuments(serviceQuotation.id)
      .then((resource) => {
        const docs = resource as DocumentProps[];
        setDocuments(
          Array.isArray(docs)
            ? docs
                .filter((document) => document.origin === "scope_of_work")
                .sort((a, b) => Number(b.is_main) - Number(a.is_main))
            : [],
        );
      })
      .catch(() => {
        setDocuments([]);
      });
  }, [serviceQuotation.id]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const legacyScopeOfWork = serviceQuotation.scope_of_work?.filename
    ? [
        {
          created_at: serviceQuotation.scope_of_work.created_at,
          filename: serviceQuotation.scope_of_work.filename,
          id: `legacy-${serviceQuotation.scope_of_work.id}`,
          is_main: documents.length === 0,
          mail_user: "",
          url: `/api/v3/service_quotations/${serviceQuotation.id}/scope_of_work/download`,
        },
      ]
    : [];

  const scopeOfWorkDocuments: ScopeOfWorkDocument[] = [
    ...legacyScopeOfWork,
    ...documents,
  ];

  const handleDownload = async (
    url: string,
    filename: string,
  ): Promise<void> => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Grid size={12} sx={{ marginTop: 2 }}>
      <Typography variant="body2">
        {t("serviceQuotation.attachmentsSection.scopeOfWork")}
      </Typography>
      <Card sx={{ margin: 2, minWidth: 275 }}>
        <Table aria-label="scope of work table" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ minWidth: 170 }}>
                {t("filename")}
              </TableCell>
              <TableCell align="center" style={{ minWidth: 170 }}>
                #
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scopeOfWorkDocuments.length > 0 ? (
              scopeOfWorkDocuments.map((document) => (
                <TableRow hover key={document.id} role="checkbox" tabIndex={-1}>
                  <TableCell align="center">
                    <Typography color="textSecondary" sx={{ fontSize: 14 }}>
                      {document.filename}
                    </Typography>
                    {!!document.is_main && (
                      <Typography
                        color="primary"
                        sx={{ fontSize: 12, fontWeight: 700, mt: 0.5 }}
                      >
                        {t("serviceQuotation.attachmentsSection.main")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      endIcon={<Icon icon={mdiDownload} />}
                      onClick={() =>
                        handleDownload(document.url, document.filename)
                      }
                      sx={{ width: 200 }}
                    >
                      {t("file")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={2}>
                  <Typography color="text.secondary" variant="body2">
                    {t("attachments.noAttachments")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </Grid>
  );
};

const ServiceBidWinnerJustification: React.FC<
  ServiceBidWinnerJustificationProps
> = ({ serviceQuotation }) => {
  const { t } = useTranslation();

  return (
    <Card square sx={{ marginTop: 1 }}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid size={6}>
            <Typography variant="body2">
              {t("serviceQuotationApproval.forms.field.justification")}
            </Typography>
            <Typography variant="subtitle2">
              {serviceQuotation.last_service_bid_winner.justification}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="body2">
              {t("serviceNegotiation.dialogWinner.typeOfRfq")}
            </Typography>
            <Typography variant="subtitle2">
              {
                serviceQuotation.last_service_bid_winner.purchase_order_type
                  .name
              }
            </Typography>
          </Grid>
        </Grid>

        <Grid size={2}>
          <Typography variant="body2">
            {t("serviceQuotationApproval.awardDocuments")}
          </Typography>
          <Typography variant="subtitle2">
            <Card sx={{ margin: 2, minWidth: 275 }}>
              <Table aria-label="sticky table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" style={{ minWidth: 170 }}>
                      {t("filename")}
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: 170 }}>
                      #
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviceQuotation.last_service_bid_winner.award_documents
                    .length > 0 &&
                    serviceQuotation.last_service_bid_winner.award_documents.map(
                      (document) => (
                        <TableRow
                          hover
                          key={document.id}
                          role="checkbox"
                          tabIndex={-1}
                        >
                          <TableCell align="center">
                            <Typography
                              color="textSecondary"
                              sx={{ fontSize: 14 }}
                            >
                              {document.filename}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              download={document.filename}
                              endIcon={<Icon icon={mdiDownload} />}
                              href={`/vbuyer/api/v2/service_bid_winners/${serviceQuotation.last_service_bid_winner.id}/get_award_document?award_document_id=${document.id}`}
                              sx={{ width: 200 }}
                              target="_blank"
                            >
                              {t("file")}
                            </Button>
                          </TableCell>
                          {/* <TableCell align="center">
                                <Switch {...label} />
                              </TableCell> */}
                        </TableRow>
                      ),
                    )}
                </TableBody>
              </Table>
            </Card>
          </Typography>
        </Grid>

        <Grid container size={12} spacing={2} sx={{ marginTop: 2 }}>
          <Grid size={12}>
            <Card sx={{ margin: 2, minWidth: 275 }}>
              <Table aria-label="single attachments table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" style={{ minWidth: 170 }}>
                      {t("filename")}
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: 170 }}>
                      #
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    {
                      attachment:
                        serviceQuotation.last_service_bid_winner
                          .requester_approval,
                      downloadPath: "get_requester_approval",
                      label: t("serviceQuotationApproval.requesterApproval"),
                    },
                    {
                      attachment:
                        serviceQuotation.last_service_bid_winner.ssu_checklist,
                      downloadPath: "get_ssu_checklist",
                      label: t("serviceQuotationApproval.ssuChecklist"),
                    },
                    {
                      attachment:
                        serviceQuotation.last_service_bid_winner.tdi_approval,
                      downloadPath: "get_tdi_approval",
                      label: t("serviceQuotationApproval.tdiApproval"),
                    },
                    {
                      attachment:
                        serviceQuotation.last_service_bid_winner
                          .single_sourcing_justification,
                      downloadPath: "get_single_sourcing_justification",
                      label: t(
                        "serviceQuotationApproval.singleSourcingJustification",
                      ),
                    },
                    {
                      attachment:
                        serviceQuotation.last_service_bid_winner
                          .simplified_procurement_strategy,
                      downloadPath: "get_simplified_procurement_strategy",
                      label: t(
                        "serviceQuotationApproval.simplifiedProcurementStrategy",
                      ),
                    },
                  ].map(({ attachment, downloadPath, label }) => (
                    <TableRow
                      hover
                      key={downloadPath}
                      role="checkbox"
                      tabIndex={-1}
                    >
                      <TableCell align="center">
                        <Typography color="textSecondary" sx={{ fontSize: 14 }}>
                          {label}
                        </Typography>
                        {!!attachment && (
                          <Typography
                            color="textSecondary"
                            sx={{ fontSize: 12 }}
                          >
                            {attachment.filename}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {attachment ? (
                          <Button
                            download={attachment.filename}
                            endIcon={<Icon icon={mdiDownload} />}
                            href={`/vbuyer/api/v2/service_bid_winners/${serviceQuotation.last_service_bid_winner.id}/${downloadPath}`}
                            sx={{ width: 200 }}
                            target="_blank"
                          >
                            {t("file")}
                          </Button>
                        ) : (
                          <Typography
                            color="textSecondary"
                            sx={{ fontSize: 14 }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Grid>
        </Grid>

        <ServiceQuotationScopeOfWorkDocuments
          serviceQuotation={serviceQuotation}
        />
      </CardContent>
    </Card>
  );
};

const FormServiceQuotationApproval: React.FC<
  FormServiceQuotationApprovalProps
> = ({ serviceQuotationApproval }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [serviceNegotiations, setServiceNegotiations] =
    useState<ServiceNegotiation[]>();

  const navigate = useNavigate();

  const schema = z.object({
    decision: z.enum(["approve", "reject"]),
    justification: z.string().min(1),
  });

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      decision: undefined,
      justification: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<z.infer<typeof schema>> = useCallback(
    (data) => {
      setLoading(true);

      updateServiceQuotationApproval(serviceQuotationApproval.id, data)
        .then(() => {
          navigate({
            params: {
              serviceQuotationId: String(
                serviceQuotationApproval.service_quotation.id,
              ),
            },
            to: "/vbuyer/service_quotations/$serviceQuotationId",
          });
          if (data.decision === "approve") {
            enqueueSnackbar(t("enqueueSnackbar.proposalApproved"));
          } else if (data.decision === "reject") {
            enqueueSnackbar(t("enqueueSnackbar.proposalReject"));
          }
        })
        .catch((error) => {
          enqueueSnackbar(error.message);
        });
    },
    [
      navigate,
      serviceQuotationApproval.id,
      serviceQuotationApproval.service_quotation.id,
      t,
    ],
  );

  const handleFetchServiceNegotiations = useCallback(
    async (filters?: ListServiceNegotiationsParams) => {
      setLoading(() => true);
      const params: ListServiceNegotiationsParams | undefined = filters
        ? {
            identifier: filters.identifier,
            pr_number: filters.pr_number,
            service_quotation_id: filters.service_quotation_id,
            status_name: filters.status_name,
          }
        : undefined;

      listServiceNegotiations(params)
        .then((sq) => {
          setServiceNegotiations(() => sq.results || []);
        })
        .finally(() => {
          setLoading(() => false);
        });
    },
    [],
  );

  useEffect(() => {
    handleFetchServiceNegotiations({
      service_quotation_id: serviceQuotationApproval.service_quotation.id,
    });
  }, [handleFetchServiceNegotiations, serviceQuotationApproval]);

  if (loading) {
    return <LoadingCentral />;
  }

  return (
    <TableContainer component={Paper} sx={{ marginTop: 1 }}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell> {t("serviceQuotation.vendor")} </TableCell>
            <TableCell align="center">
              {t("serviceNegotiation.status")}
            </TableCell>
            <TableCell align="center">
              {t("serviceNegotiation.value")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {serviceNegotiations
            ?.sort((a, b) => {
              const priceA = a.proposal?.price ?? null;
              const priceB = b.proposal?.price ?? null;

              if (priceA === null && priceB === null) return 0;
              if (priceA === null) return 1;
              if (priceB === null) return -1;
              return Number(priceA) - Number(priceB);
            })
            .map((serviceNegotiation: ServiceNegotiation) => (
              <TableRow
                key={serviceNegotiation.id}
                sx={{
                  "&:last-child td, &:last-child th": {
                    border: 0,
                  },
                }}
              >
                <TableCell>
                  {serviceNegotiation.vendor.fullvendorname}
                </TableCell>
                <TableCell align="center">
                  {serviceNegotiation.vendor.fullvendorname ===
                  serviceQuotationApproval.service_quotation.vendor_winner
                    ?.fullvendorname ? (
                    <Icon color="success" icon={mdiCheckboxMarkedCircle} />
                  ) : (
                    <Icon color="primary" icon={mdiClock} />
                  )}
                </TableCell>
                <TableCell align="center">
                  {serviceNegotiation?.proposal?.currency.unit || ""}{" "}
                  {serviceNegotiation?.proposal?.price || "-"}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <hr />

      {serviceQuotationApproval.service_quotation.status.name ===
        "Waiting Approval" && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            alignItems: "center",
            borderRadius: "3px",
            display: "flex",
            flexDirection: "column",
            marginBottom: "10px",
            padding: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "space-between",
              marginBottom: "10px",
              width: "100%",
            }}
          >
            <Controller
              control={control}
              name="justification"
              render={({ field }) => (
                <TextField
                  label={t(
                    "serviceQuotationApproval.forms.field.justification",
                  )}
                  multiline
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  required
                  rows={3}
                  size="small"
                  sx={{ width: "100%" }}
                  value={field.value}
                  variant="outlined"
                />
              )}
            />
            {/* <Controller
              control={control}
              name="decision"
              render={({ field }) => (
                <TextField
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  size="small"
                  type="hidden"
                  value={field.value}
                />
              )}
            /> */}
          </div>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <Button
              color="energyRed"
              loading={loading}
              loadingPosition="center"
              onClick={() => setValue("decision", "reject")}
              style={{ justifyContent: "center", marginTop: "8px" }}
              type="submit"
              variant="contained"
            >
              {t("serviceQuotationApproval.forms.button.reject")}
            </Button>
            <Button
              color="success"
              loading={loading}
              loadingPosition="center"
              onClick={() => setValue("decision", "approve")}
              style={{ justifyContent: "center", marginTop: "8px" }}
              type="submit"
              variant="contained"
            >
              {t("serviceQuotationApproval.forms.button.approve")}
            </Button>
          </div>
        </form>
      )}
    </TableContainer>
  );
};

const ShowServiceQuotationApproval: React.FC = () => {
  const serviceQuotationApproval = Route.useLoaderData();
  const { t } = useTranslation();

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          padding: "0px 20px",
        }}
      >
        <div>
          <h2>{t("serviceQuotation.title")}</h2>
          <h3 style={{ marginTop: "-10px" }}>
            <Link
              params={{
                serviceQuotationId: String(
                  serviceQuotationApproval?.service_quotation?.id,
                ),
              }}
              to="/vbuyer/service_quotations"
            >
              #{serviceQuotationApproval?.service_quotation?.identifier}
            </Link>
          </h3>
        </div>
      </Box>

      <Container sx={{ marginBottom: 4 }}>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar color="white" position="static">
            <Toolbar variant="dense">
              <Typography component="div" sx={{ flexGrow: 1 }} variant="h6">
                {t("details")}
              </Typography>
            </Toolbar>
          </AppBar>
        </Box>
        <Card square sx={{ marginTop: 1 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceNegotiation.prNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotationApproval?.service_quotation?.pr_item
                    ?.pr_number || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.itemNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotationApproval?.service_quotation?.pr_item
                    ?.item_number || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceQuotation.requisitionDate")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotationApproval?.service_quotation?.pr_item
                    ?.requisition_date
                    ? t(
                        "intlDate",
                        i18nextParseToDate(
                          serviceQuotationApproval?.service_quotation?.pr_item
                            .requisition_date,
                        ),
                      )
                    : ""}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceNegotiation.shortDescription")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotationApproval?.service_quotation
                    ?.short_description || ""}
                </Typography>
              </Grid>

              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.plantNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotationApproval?.service_quotation?.pr_item?.plant
                    ?.plant_number || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.quantity")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotationApproval?.service_quotation?.pr_item
                    ?.quantity || ""}
                </Typography>
              </Grid>

              <Grid size={12}>
                <OutlinedInput
                  disabled
                  fullWidth
                  multiline
                  placeholder={t("serviceNegotiation.description")}
                  rows={5}
                  style={{
                    marginTop: "16px",
                  }}
                  sx={{ fontSize: "0.9em" }}
                  value={
                    serviceQuotationApproval?.service_quotation?.long_text || ""
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <Container sx={{ marginBottom: 4 }}>
        <BasicBar title={t("serviceQuotationApproval.award_infos")} />
        {!!serviceQuotationApproval?.service_quotation && (
          <ServiceBidWinnerJustification
            serviceQuotation={serviceQuotationApproval?.service_quotation}
          />
        )}
      </Container>

      <Container>
        <BasicBar title={t("proposal.proposals")} />

        {serviceQuotationApproval && (
          <FormServiceQuotationApproval
            serviceQuotationApproval={serviceQuotationApproval}
          />
        )}
      </Container>

      <Container sx={{ marginBottom: 4, marginTop: 4 }}>
        <BasicBar title={t("serviceQuotationApproval.activity.title")} />

        {!!serviceQuotationApproval?.service_quotation && (
          <ServiceNegotiationAudits
            serviceQuotation={serviceQuotationApproval.service_quotation}
          />
        )}
      </Container>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/service_quotation_approvals/$serviceQuotationApprovalId/",
)({
  component: ShowServiceQuotationApproval,
  loader: ({ params: { serviceQuotationApprovalId } }) =>
    getServiceQuotationApproval(parseInt(serviceQuotationApprovalId, 10)),
});
